/* eslint-disable-next-line no-restricted-imports */
import * as t from 'io-ts';

import { tId } from './common';
import { tPublicImage } from './schemas';
import {
  tBrandCondition,
  tBrandIdCondition,
  tKindCondition,
  tProductThresholdConditions,
  tSpecialConditions,
  tWeightCondition,
} from './specialConditions';

export const SpecialStatus = {
  live: 'live',
  upcoming: 'upcoming',
  expired: 'expired',
  disabled: 'disabled',
  archived: 'archived',
} as const;

export type SpecialStatus = keyof typeof SpecialStatus;

export const tSchedule = t.intersection([
  t.type({
    enabled_monday_all_day: t.union([t.boolean, t.undefined]),
    enabled_tuesday_all_day: t.union([t.boolean, t.undefined]),
    enabled_wednesday_all_day: t.union([t.boolean, t.undefined]),
    enabled_thursday_all_day: t.union([t.boolean, t.undefined]),
    enabled_friday_all_day: t.union([t.boolean, t.undefined]),
    enabled_saturday_all_day: t.union([t.boolean, t.undefined]),
    enabled_sunday_all_day: t.union([t.boolean, t.undefined]),
    start_time_monday: t.union([t.string, t.null, t.undefined]),
    start_time_tuesday: t.union([t.string, t.null, t.undefined]),
    start_time_wednesday: t.union([t.string, t.null, t.undefined]),
    start_time_thursday: t.union([t.string, t.null, t.undefined]),
    start_time_friday: t.union([t.string, t.null, t.undefined]),
    start_time_saturday: t.union([t.string, t.null, t.undefined]),
    start_time_sunday: t.union([t.string, t.null, t.undefined]),
    end_time_monday: t.union([t.string, t.null, t.undefined]),
    end_time_tuesday: t.union([t.string, t.null, t.undefined]),
    end_time_wednesday: t.union([t.string, t.null, t.undefined]),
    end_time_thursday: t.union([t.string, t.null, t.undefined]),
    end_time_friday: t.union([t.string, t.null, t.undefined]),
    end_time_saturday: t.union([t.string, t.null, t.undefined]),
    end_time_sunday: t.union([t.string, t.null, t.undefined]),
    use_store_close_time_for_end: t.union([t.boolean, t.undefined]),
  }),
  t.partial({
    enabled: t.union([t.boolean, t.undefined]),
    start_time: t.union([t.string, t.null, t.undefined]),
    end_time: t.union([t.string, t.null, t.undefined]),
    enabled_monday: t.union([t.boolean, t.null, t.undefined]),
    enabled_tuesday: t.union([t.boolean, t.null, t.undefined]),
    enabled_wednesday: t.union([t.boolean, t.null, t.undefined]),
    enabled_thursday: t.union([t.boolean, t.null, t.undefined]),
    enabled_friday: t.union([t.boolean, t.null, t.undefined]),
    enabled_saturday: t.union([t.boolean, t.null, t.undefined]),
    enabled_sunday: t.union([t.boolean, t.null, t.undefined]),
  }),
]);

const tAbbreviatedSpecialV2Optional = t.partial({
  archived: t.boolean,
  // TODO: This can return either a UNIX timestamp (for global) or formatted date string (for store-specific), might want to consolidate
  archived_at: t.union([t.number, t.string, t.null]),
  conditions: t.union([tSpecialConditions, t.undefined]),
  description: t.union([t.string, t.null]),
  promo_code: t.union([t.string, t.null]),
  enabled: t.boolean,
  promo_code_max_number_of_uses: t.union([t.number, t.null]),
  multiple_use_promo_code: t.union([t.boolean, t.null]),
  reservation_modes: t.union([
    t.partial({
      kiosk: t.union([t.boolean, t.undefined]),
      delivery: t.union([t.boolean, t.undefined]),
      pickup: t.union([t.boolean, t.undefined]),
      curbside: t.union([t.boolean, t.undefined]),
    }),
    t.undefined,
  ]),
  terms: t.union([t.string, t.null]),
  pos_synced: t.boolean,
  pos_source: t.union([t.string, t.null]),
  pos_special_id: t.union([t.string, t.null]),
  pos_special_link: t.union([t.string, t.null]),
  discount_dollar_amount: t.number,
  discount_percent: t.number,
  discount_target_price: t.number,
  store_ids: t.array(tId),
  next_occurrence: t.union([t.number, t.null, t.string]),
  schedule: tSchedule,
});

const tAbbreviatedSpecialV2Required = t.type({
  id: tId,
  title: t.string,
  discount_type: t.string,
  start_time: t.union([t.string, t.null]),
  end_time: t.union([t.string, t.null]),
  detail_summary: t.array(t.string),
  special_type: t.keyof({
    product: null,
    cart_total: null,
    qualified_group: null,
    bundle: null,
    bulk_pricing: null,
    spending_threshold: null,
  }),
  store_specific: t.boolean,
});

const tAbbreviatedSpecialV2 = t.intersection([
  tAbbreviatedSpecialV2Optional,
  tAbbreviatedSpecialV2Required,
]);

const tSpecialsResponseMeta = t.type({
  page: t.number,
  page_total: t.number,
  per_page: t.number,
  total: t.number,
  all_ids: t.array(t.number),
  counts_by_status: t.type({
    live: t.number,
    upcoming: t.number,
    expired: t.number,
    disabled: t.number,
    archived: t.number,
  }),
});

export const tSpecialsIndexResponse = t.type({
  specials: t.array(tAbbreviatedSpecialV2),
  meta: tSpecialsResponseMeta,
});

export const tSpecialDetailResponse = t.type({
  special: tAbbreviatedSpecialV2,
});

const tSpecialRuleItemV2 = t.partial({
  brands: t.union([t.array(tBrandCondition), t.undefined, t.null]),
  brand_ids: t.union([t.array(tBrandIdCondition), t.undefined, t.null]),
  product_ids: t.union([t.array(t.number), t.undefined, t.null]),
  kinds: t.union([t.array(tKindCondition), t.undefined, t.null]),
  lineages: t.union([t.array(t.string), t.undefined, t.null]),
  weights: t.union([t.array(tWeightCondition), t.undefined, t.null]),
});

const tSpendingThresholdRuleset = t.partial({
  includes: t.union([t.array(tSpecialRuleItemV2), t.undefined, t.null]),
  excludes: t.union([t.array(tSpecialRuleItemV2), t.undefined, t.null]),
  product_threshold: t.union([
    tProductThresholdConditions,
    t.undefined,
    t.null,
  ]),
});

// NOTE: There is a tSpecialRules in shared/models but that structure is not right
export const tSpecialRulesV2 = t.partial({
  includes: t.union([t.array(tSpecialRuleItemV2), t.undefined, t.null]),
  excludes: t.union([t.array(tSpecialRuleItemV2), t.undefined, t.null]),
  product_threshold: t.union([
    tProductThresholdConditions,
    t.undefined,
    t.null,
  ]),
  settings: t.union([
    t.partial({
      cart_threshold: t.number,
      copy_conditions_for_discounted_products: t.boolean,
      allow_discounts_on_required_products: t.boolean,
      max_number_of_discounted_products: t.number,
      max_applications_per_cart: t.number,
      exclude_product_specials_from_bundle_qualifiers: t.boolean,
      apply_target_price_per_unit: t.boolean,
      // bulk_pricing parity payload
      target_weight: t.string,
      // bundle parity payload
      threshold_number_of_items_in_cart: t.number,
    }),
    t.undefined,
    t.null,
  ]),
  max_applications_per_cart: t.union([t.number, t.null, t.undefined]),
  max_number_of_discounted_products: t.union([t.number, t.null, t.undefined]),
  threshold_number_of_items_in_cart: t.union([t.number, t.null, t.undefined]),
  dependent: t.union([
    t.partial({
      max_number_of_discounted_products: t.union([
        t.number,
        t.null,
        t.undefined,
      ]),
    }),
    t.undefined,
    t.null,
  ]),
  independent: t.union([
    t.partial({
      threshold_number_of_items_in_cart: t.union([
        t.number,
        t.null,
        t.undefined,
      ]),
    }),
    t.undefined,
    t.null,
  ]),
  // qualifier/discountable accept either a single rule set (spending_threshold)
  // or an array of rule sets (bundle parity payload mirroring the server's RulesFromConditions output)
  qualifier: t.union([
    tSpendingThresholdRuleset,
    t.array(tSpendingThresholdRuleset),
    t.undefined,
    t.null,
  ]),
  discountable: t.union([
    tSpendingThresholdRuleset,
    t.array(tSpendingThresholdRuleset),
    t.undefined,
    t.null,
  ]),
  // qualified_group parity payload
  base: t.union([
    t.partial({
      type: t.string,
      required_age: t.number,
    }),
    t.undefined,
    t.null,
  ]),
});

const t_StoreSpecialV2 = t.intersection([
  tAbbreviatedSpecialV2,
  t.type({
    conditions: tSpecialConditions,
    custom_badge: t.union([t.null, t.string]),
    photo: t.union([t.null, tPublicImage]),
    rules: tSpecialRulesV2,
    stacking_setting: t.union([
      t.literal('yes'),
      t.literal('no'),
      t.literal('combinable'),
    ]),
    schedule: tSchedule,
    store_specific: t.boolean,
    use_store_stacking_setting: t.union([t.undefined, t.boolean]),
    hidden_from_row: t.boolean,
    hidden_from_page: t.boolean,
  }),
]);

export type AbbreviatedSpecialV2 = t.TypeOf<typeof tAbbreviatedSpecialV2>;
export type SpecialsIndexResponse = t.TypeOf<typeof tSpecialsIndexResponse>;
export type SpecialRulesV2 = t.TypeOf<typeof tSpecialRulesV2>;
export type StoreSpecialV2 = t.TypeOf<typeof t_StoreSpecialV2>;

// ----------------- GLOBAL ----------------- \\

const tGlobalSpecialStore = t.type({
  _key: t.union([t.string, t.undefined]),
  address: t.union([t.string, t.undefined]),
  address2: t.union([t.string, t.null, t.undefined]),
  can_edit: t.union([t.boolean, t.undefined]),
  city: t.union([t.string, t.null, t.undefined]),
  enabled: t.union([t.boolean, t.undefined]),
  id: t.union([t.string, t.number]),
  name: t.string,
  recreational: t.union([t.boolean, t.undefined]),
  state: t.union([t.string, t.null, t.undefined]),
  zip: t.union([t.string, t.undefined]),
});
export type GlobalSpecialStore = t.TypeOf<typeof tGlobalSpecialStore>;

const tAbbreviatedGlobalSpecialV2Optional = t.partial({
  archived: t.boolean,
  // TODO: This can return either a UNIX timestamp (for global) or formatted date string (for store-specific), might want to consolidate
  archived_at: t.union([t.number, t.string, t.null]),
  description: t.union([t.string, t.null]),
  promo_code: t.union([t.string, t.null]),
  enabled: t.boolean,
  promo_code_max_number_of_uses: t.union([t.number, t.null]),
  multiple_use_promo_code: t.union([t.boolean, t.null]),
  reservation_modes: t.union([
    t.partial({
      kiosk: t.union([t.boolean, t.undefined]),
      delivery: t.union([t.boolean, t.undefined]),
      pickup: t.union([t.boolean, t.undefined]),
      curbside: t.union([t.boolean, t.undefined]),
    }),
    t.undefined,
  ]),
  terms: t.union([t.string, t.null]),
  pos_synced: t.boolean,
  pos_source: t.union([t.string, t.null]),
  pos_special_id: t.union([t.string, t.null]),
  pos_special_link: t.union([t.string, t.null]),
  discount_dollar_amount: t.number,
  discount_percent: t.number,
  discount_target_price: t.number,
  stores: t.array(tGlobalSpecialStore),
  next_occurrence: t.union([t.number, t.null, t.string]),
});

const tAbbreviatedGlobalSpecialsV2 = t.intersection([
  tAbbreviatedGlobalSpecialV2Optional,
  tAbbreviatedSpecialV2Required,
]);

export const tGlobalSpecialDetailResponse = t.type({
  special: tAbbreviatedGlobalSpecialsV2,
});

export const tGlobalSpecialsIndexResponse = t.type({
  specials: t.array(tAbbreviatedGlobalSpecialsV2),
  meta: tSpecialsResponseMeta,
});

const t_GlobalSpecialV2 = t.intersection([
  tAbbreviatedGlobalSpecialsV2,
  t.type({
    conditions: tSpecialConditions,
    custom_badge: t.union([t.null, t.string]),
    photo: t.union([t.null, tPublicImage]),
    rules: tSpecialRulesV2,
    stacking_setting: t.union([
      t.literal('yes'),
      t.literal('no'),
      t.literal('combinable'),
    ]),
    schedule: tSchedule,
    hidden_from_row: t.boolean,
    hidden_from_page: t.boolean,
  }),
]);

export type AbbreviatedGlobalSpecialsV2 = t.TypeOf<
  typeof tAbbreviatedGlobalSpecialsV2
>;
export type GlobalSpecialsIndexResponse = t.TypeOf<
  typeof tGlobalSpecialsIndexResponse
>;
export type GlobalSpecialV2 = t.TypeOf<typeof t_GlobalSpecialV2>;
